import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiGet } from '../api';
import { isSubscriptionOnboardingAllowedPath } from '../utils/authRedirect';

type OnboardingRouting = {
  required?: boolean;
  path?: string | null;
};

function readCachedOnboarding(): OnboardingRouting | null {
  try {
    const raw = localStorage.getItem('srs_user');
    if (!raw) return null;
    const user = JSON.parse(raw) as { auth_routing?: { subscription_onboarding?: OnboardingRouting } };
    const onboarding = user?.auth_routing?.subscription_onboarding;
    if (!onboarding) return null;
    return onboarding;
  } catch {
    return null;
  }
}

/**
 * Redirects authenticated users who must complete post-signup subscription selection.
 * Uses auth_routing cached on srs_user (from /auth/me or login) before calling the API.
 */
export default function SubscriptionOnboardingGate() {
  const navigate = useNavigate();
  const location = useLocation();

  const enforce = useCallback(async (pathname: string) => {
    if (isSubscriptionOnboardingAllowedPath(pathname)) {
      return;
    }
    if (!localStorage.getItem('srs_user')) {
      return;
    }

    const cached = readCachedOnboarding();
    if (cached && cached.required === false) {
      return;
    }
    if (cached?.required && cached.path && pathname !== cached.path) {
      navigate(cached.path, { replace: true });
      return;
    }

    try {
      const res = await apiGet<{
        success: boolean;
        data?: { required?: boolean; path?: string };
      }>('/subscriptions/signup/onboarding');
      if (!res.success || !res.data) return;
      const stored = localStorage.getItem('srs_user');
      if (stored) {
        const user = JSON.parse(stored);
        user.auth_routing = { ...user.auth_routing, subscription_onboarding: res.data };
        localStorage.setItem('srs_user', JSON.stringify(user));
      }
      if (!res.data.required || !res.data.path) return;
      if (pathname !== res.data.path) {
        navigate(res.data.path, { replace: true });
      }
    } catch {
      // Cookie may be absent; ignore.
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await enforce(location.pathname);
    })();
    return () => { cancelled = true; };
  }, [location.pathname, enforce]);

  useEffect(() => {
    const onUserUpdated = () => {
      enforce(location.pathname);
    };
    window.addEventListener('userUpdated', onUserUpdated);
    return () => window.removeEventListener('userUpdated', onUserUpdated);
  }, [location.pathname, enforce]);

  return null;
}
